import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { Layout } from '@/components/layout';
import Hero from '@/sections/Hero';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { Trans, useTranslation } from 'react-i18next';
import { FaqIntro } from '@/sections/FaqIntro';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import { LINKS } from '@/constants/links';
import { SectionHeadline } from '@/components/SectionHeadline';
import { Link } from '@/components/links';
import { AppstoreButton, Button, GooglePlayButton } from '@/components/buttons';
import { useRouter } from 'next/router';
import { FILES } from '@/constants/files';
import { Benefits } from '@/sections/rewards/Benefits';
import { Cards } from '@/sections/rewards/Cards';
import Image from 'next/image';
import MoreInfoSection from '@/sections/MoreInfo';
import IconCard from '@/assets/icons/heroIcons/Card-front.svg';
import IconThird from '@/assets/icons/heroIcons/Icon-third.svg';
import IconDeferredPayment from '@/assets/icons/heroIcons/Deferred-payment.svg';
import { Discounts } from '@/sections/rewards/Discounts';
import { mergeClasses } from '@/utils/style';
import React, { ReactNode } from 'react';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { SeoHeader } from '@/components/SeoHeader';
import { Text, Headline } from '@/components/typography';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { AdvantageBoxes } from '@/components/boxes';
import IconMaxi from '@/assets/icons/heroIcons/Maxi.svg';
function CenterText(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = mergeClasses("mx-auto max-w-192 px-4 text-center text-gray-800", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <Text as="p" variant="p2" className={t1}>{children}</Text>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
interface Props extends IntlProps {
  pageBanner: PageBanner | null;
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(props) {
  const $ = _c(177);
  usePageView("rewards");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["core", "rewards"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    pageBanner,
    menuPromoBanners
  } = props;
  const {
    locale
  } = useRouter();
  let t1;
  if ($[1] !== t) {
    t1 = t("rewards:seo.title");
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t) {
    t2 = t("rewards:seo.description");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <SeoHeader title={t1} description={t2} />;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = pageBanner?.videoId;
  let t5;
  if ($[8] !== pageBanner?.title || $[9] !== t) {
    t5 = pageBanner?.title ?? t("rewards:heroSection.title");
    $[8] = pageBanner?.title;
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== pageBanner?.text || $[12] !== t) {
    t6 = pageBanner?.text ?? t("rewards:heroSection.paragraph");
    $[11] = pageBanner?.text;
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== pageBanner || $[15] !== t) {
    t7 = pageBanner ? pageBanner.buttons : [{
      ariaLabel: t("rewards:heroSection.buttonPrimary"),
      label: t("rewards:heroSection.buttonPrimary"),
      to: LINKS().registration,
      testId: "rewardsHeroPrimaryCTA",
      analyticsParam: "rewardsHeroPrimaryCTA",
      variant: "primary"
    }];
    $[14] = pageBanner;
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = pageBanner?.image?.url ?? "/images/sections/rewards/cs/hero/hero.png";
  let t9;
  if ($[17] !== pageBanner?.image?.alt || $[18] !== t) {
    t9 = pageBanner?.image?.alt ?? t("rewards:heroSection.imageAlt");
    $[17] = pageBanner?.image?.alt;
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8 || $[21] !== t9) {
    t10 = {
      url: t8,
      alt: t9
    };
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const t11 = pageBanner?.variant || "mockupMobile";
  let t12;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t4 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7) {
    t12 = <Hero videoId={t4} title={t5} text={t6} buttons={t7} image={t10} variant={t11} />;
    $[23] = t10;
    $[24] = t11;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="bg-blue-50"><SectionDelimiter color="bg-blue-100" background="bg-transparent" /></div>;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  let t15;
  let t16;
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="bgCircle absolute top-8 right-0 hidden translate-x-1/3 rotate-270 lg:block" />;
    t15 = <div className="bgCircle absolute top-1/4 left-0 -translate-x-1/4 translate-y-12 -rotate-90" />;
    t16 = <div className="bgCircle absolute top-1/2 right-0 translate-x-1/3 scale-75 rotate-270" />;
    t17 = <Benefits />;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
    $[34] = t17;
  } else {
    t14 = $[31];
    t15 = $[32];
    t16 = $[33];
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t) {
    t18 = t("rewards:exchangeCoins.title");
    $[35] = t;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t18) {
    t19 = <SectionHeadline className="mt-28 mb-5 whitespace-pre-wrap lg:mt-40" anchorKey="rewards.exchangeCoins.title">{t18}</SectionHeadline>;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t) {
    t20 = t("rewards:exchangeCoins.paragraph");
    $[39] = t;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t20) {
    t21 = <CenterText>{t20}</CenterText>;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  let t23;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Discounts />;
    t23 = <SectionDelimiter color="bg-blue-100" background="bg-transparent" />;
    $[43] = t22;
    $[44] = t23;
  } else {
    t22 = $[43];
    t23 = $[44];
  }
  let t24;
  if ($[45] !== t) {
    t24 = t("rewards:benefits.title");
    $[45] = t;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== t24) {
    t25 = <Headline as="h3" variant="h4" anchorKey="rewards.benefits.title" className="mt-20 mb-3 text-center whitespace-pre-wrap lg:mt-28 lg:mb-5">{t24}</Headline>;
    $[47] = t24;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] !== t) {
    t26 = t("rewards:benefits.paragraph");
    $[49] = t;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] !== t26) {
    t27 = <CenterText>{t26}</CenterText>;
    $[51] = t26;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== t) {
    t28 = t("rewards:benefits.items.0.paragraph");
    $[53] = t;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== t) {
    t29 = t("rewards:benefits.items.0.title");
    $[55] = t;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  let t30;
  if ($[57] !== t28 || $[58] !== t29) {
    t30 = {
      image: "/images/sections/rewards/cs/benefits/0.png",
      text: t28,
      title: t29
    };
    $[57] = t28;
    $[58] = t29;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Link analyticsParam="rewardsBenefitsRegistration" testId="rewardsBenefitsRegistration" to={LINKS().whereToShop} />;
    $[60] = t31;
  } else {
    t31 = $[60];
  }
  let t32;
  let t33;
  if ($[61] !== t) {
    t32 = <Trans t={t} i18nKey="rewards:benefits.items.1.paragraph">{t31}</Trans>;
    t33 = t("rewards:benefits.items.1.title");
    $[61] = t;
    $[62] = t32;
    $[63] = t33;
  } else {
    t32 = $[62];
    t33 = $[63];
  }
  let t34;
  if ($[64] !== t32 || $[65] !== t33) {
    t34 = {
      image: "/images/sections/rewards/cs/benefits/1.png",
      text: t32,
      title: t33
    };
    $[64] = t32;
    $[65] = t33;
    $[66] = t34;
  } else {
    t34 = $[66];
  }
  let t35;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <Link analyticsParam="rewardsBenefitsDeferredPayment" testId="rewardsBenefitsDeferredPayment" to={LINKS().deferredPayment} />;
    $[67] = t35;
  } else {
    t35 = $[67];
  }
  let t36;
  let t37;
  if ($[68] !== t) {
    t36 = <Trans t={t} i18nKey="rewards:benefits.items.2.paragraph">{t35}</Trans>;
    t37 = t("rewards:benefits.items.2.title");
    $[68] = t;
    $[69] = t36;
    $[70] = t37;
  } else {
    t36 = $[69];
    t37 = $[70];
  }
  let t38;
  if ($[71] !== t36 || $[72] !== t37) {
    t38 = {
      image: "/images/sections/rewards/cs/benefits/2.png",
      text: t36,
      title: t37
    };
    $[71] = t36;
    $[72] = t37;
    $[73] = t38;
  } else {
    t38 = $[73];
  }
  let t39;
  if ($[74] !== t) {
    t39 = t("rewards:benefits.items.3.paragraph");
    $[74] = t;
    $[75] = t39;
  } else {
    t39 = $[75];
  }
  let t40;
  if ($[76] !== t) {
    t40 = t("rewards:benefits.items.3.title");
    $[76] = t;
    $[77] = t40;
  } else {
    t40 = $[77];
  }
  let t41;
  if ($[78] !== t39 || $[79] !== t40) {
    t41 = {
      image: "/images/sections/rewards/cs/benefits/3.png",
      text: t39,
      title: t40
    };
    $[78] = t39;
    $[79] = t40;
    $[80] = t41;
  } else {
    t41 = $[80];
  }
  let t42;
  if ($[81] !== t30 || $[82] !== t34 || $[83] !== t38 || $[84] !== t41) {
    t42 = <div className="relative z-10 -mx-4 md:m-0"><AdvantageBoxes className="mx-4 mt-12 mb-6 lg:mx-auto lg:mb-12" items={[t30, t34, t38, t41]} /></div>;
    $[81] = t30;
    $[82] = t34;
    $[83] = t38;
    $[84] = t41;
    $[85] = t42;
  } else {
    t42 = $[85];
  }
  let t43;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = LINKS();
    $[86] = t43;
  } else {
    t43 = $[86];
  }
  const t44 = t43.registration;
  let t45;
  if ($[87] !== t) {
    t45 = t("rewards:benefits.button");
    $[87] = t;
    $[88] = t45;
  } else {
    t45 = $[88];
  }
  let t46;
  if ($[89] !== t45) {
    t46 = <Button to={t44} analyticsParam="rewardsBenefitRegistration" testId="rewardsBenefitRegistration" variant="primary" className="mx-auto mb-6 lg:mb-8" size="lg">{t45}</Button>;
    $[89] = t45;
    $[90] = t46;
  } else {
    t46 = $[90];
  }
  let t47;
  if ($[91] !== locale) {
    t47 = FILES(locale);
    $[91] = locale;
    $[92] = t47;
  } else {
    t47 = $[92];
  }
  let t48;
  if ($[93] !== t47.loyaltyProgramRules) {
    t48 = <Link target="_blank" analyticsParam="rewardsBenefitsConditions" testId="rewardsBenefitsConditions" to={t47.loyaltyProgramRules} />;
    $[93] = t47.loyaltyProgramRules;
    $[94] = t48;
  } else {
    t48 = $[94];
  }
  let t49;
  if ($[95] !== t || $[96] !== t48) {
    t49 = <div className="mx-auto max-w-132"><Text variant="p4" className="px-4 text-center text-gray-600"><Trans t={t} i18nKey="rewards:benefits.conditions">{t48}</Trans></Text></div>;
    $[95] = t;
    $[96] = t48;
    $[97] = t49;
  } else {
    t49 = $[97];
  }
  let t50;
  if ($[98] !== t19 || $[99] !== t21 || $[100] !== t25 || $[101] !== t27 || $[102] !== t42 || $[103] !== t46 || $[104] !== t49) {
    t50 = <div className="relative z-30 overflow-x-hidden bg-blue-100 pt-14 pb-14 lg:pt-20 lg:pb-40">{t14}{t15}{t16}{t17}{t19}{t21}{t22}{t23}{t25}{t27}{t42}{t46}{t49}</div>;
    $[98] = t19;
    $[99] = t21;
    $[100] = t25;
    $[101] = t27;
    $[102] = t42;
    $[103] = t46;
    $[104] = t49;
    $[105] = t50;
  } else {
    t50 = $[105];
  }
  let t51;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <SectionDelimiter color="bg-blue-50" background="bg-blue-100" />;
    $[106] = t51;
  } else {
    t51 = $[106];
  }
  let t52;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <Cards />;
    $[107] = t52;
  } else {
    t52 = $[107];
  }
  let t53;
  if ($[108] !== t) {
    t53 = t("rewards:mobApp.imageDescription");
    $[108] = t;
    $[109] = t53;
  } else {
    t53 = $[109];
  }
  let t54;
  if ($[110] !== t53) {
    t54 = <Image src="/images/sections/rewards/cs/app.png" alt={t53} width={600} height={603} className="mx-auto -mb-6 lg:m-0 lg:-mb-10" />;
    $[110] = t53;
    $[111] = t54;
  } else {
    t54 = $[111];
  }
  let t55;
  if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <b className="text-blue-500" />;
    $[112] = t55;
  } else {
    t55 = $[112];
  }
  let t56;
  if ($[113] !== t) {
    t56 = <Headline as="h3" anchorKey="rewards.mobApp.title" className="mb-5 text-center lg:text-left"><Trans t={t} i18nKey="rewards:mobApp.title">{t55}</Trans></Headline>;
    $[113] = t;
    $[114] = t56;
  } else {
    t56 = $[114];
  }
  let t57;
  if ($[115] !== t) {
    t57 = t("rewards:mobApp.paragraph");
    $[115] = t;
    $[116] = t57;
  } else {
    t57 = $[116];
  }
  let t58;
  if ($[117] !== t57) {
    t58 = <Text variant="p2" className="text-center text-gray-800 lg:text-left">{t57}</Text>;
    $[117] = t57;
    $[118] = t58;
  } else {
    t58 = $[118];
  }
  let t59;
  if ($[119] !== t) {
    t59 = t("rewards:mobApp.imageDescription");
    $[119] = t;
    $[120] = t59;
  } else {
    t59 = $[120];
  }
  let t60;
  if ($[121] !== t59) {
    t60 = <Image src={`/images/mobileApp/${process.env.NEXT_PUBLIC_LANGUAGE}/qr-code.png`} alt={t59} width={120} height={120} className="hidden lg:block" />;
    $[121] = t59;
    $[122] = t60;
  } else {
    t60 = $[122];
  }
  let t61;
  if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = <div className="flex w-full flex-row items-center justify-center gap-2 lg:flex-col lg:items-start lg:justify-between"><AppstoreButton mobileVariant="full" /><GooglePlayButton mobileVariant="full" /></div>;
    $[123] = t61;
  } else {
    t61 = $[123];
  }
  let t62;
  if ($[124] !== t60) {
    t62 = <div className="mt-10 flex gap-8 lg:mt-16">{t60}{t61}</div>;
    $[124] = t60;
    $[125] = t62;
  } else {
    t62 = $[125];
  }
  let t63;
  if ($[126] !== t56 || $[127] !== t58 || $[128] !== t62) {
    t63 = <div>{t56}{t58}{t62}</div>;
    $[126] = t56;
    $[127] = t58;
    $[128] = t62;
    $[129] = t63;
  } else {
    t63 = $[129];
  }
  let t64;
  if ($[130] !== t54 || $[131] !== t63) {
    t64 = <div className="relative bg-blue-50 pt-14 pb-14 lg:pt-20 lg:pb-20">{t52}<section className="mt-12 mb-4 grid max-w-7xl items-center gap-4 px-4 lg:mx-auto lg:mt-40 lg:mb-20 lg:grid-cols-2 lg:gap-12">{t54}{t63}</section></div>;
    $[130] = t54;
    $[131] = t63;
    $[132] = t64;
  } else {
    t64 = $[132];
  }
  let t65;
  if ($[133] === Symbol.for("react.memo_cache_sentinel")) {
    t65 = <SectionDelimiter color="bg-blue-100" background="bg-blue-50" />;
    $[133] = t65;
  } else {
    t65 = $[133];
  }
  let t66;
  if ($[134] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = <Link to="/caste-dotazy/odmeny" analyticsParam="faqRewards" testId="faqRewards" />;
    $[134] = t66;
  } else {
    t66 = $[134];
  }
  let t67;
  if ($[135] !== t) {
    t67 = <div className="relative z-10 py-10 lg:py-20"><FaqIntro text={<Trans i18nKey="rewards:faq.text" t={t}>{t66}</Trans>} /></div>;
    $[135] = t;
    $[136] = t67;
  } else {
    t67 = $[136];
  }
  let t68;
  if ($[137] === Symbol.for("react.memo_cache_sentinel")) {
    t68 = <IconDeferredPayment className="fillButtonIcon" />;
    $[137] = t68;
  } else {
    t68 = $[137];
  }
  let t69;
  if ($[138] !== t) {
    t69 = t("core:products.deferredPayment");
    $[138] = t;
    $[139] = t69;
  } else {
    t69 = $[139];
  }
  let t70;
  if ($[140] === Symbol.for("react.memo_cache_sentinel")) {
    t70 = LINKS();
    $[140] = t70;
  } else {
    t70 = $[140];
  }
  let t71;
  if ($[141] !== t69) {
    t71 = {
      analyticsParam: "rewardsDeferredPayment",
      icon: t68,
      testId: "rewardsDeferredPayment",
      title: t69,
      to: t70.deferredPayment
    };
    $[141] = t69;
    $[142] = t71;
  } else {
    t71 = $[142];
  }
  let t72;
  if ($[143] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = <IconMaxi className="fillButtonIcon" />;
    $[143] = t72;
  } else {
    t72 = $[143];
  }
  let t73;
  if ($[144] !== t) {
    t73 = t("core:products.revolving");
    $[144] = t;
    $[145] = t73;
  } else {
    t73 = $[145];
  }
  let t74;
  if ($[146] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = LINKS();
    $[146] = t74;
  } else {
    t74 = $[146];
  }
  let t75;
  if ($[147] !== t73) {
    t75 = {
      analyticsParam: "rewardsRevolvingApplication",
      icon: t72,
      testId: "rewardsRevolvingApplication",
      title: t73,
      to: t74.revolving
    };
    $[147] = t73;
    $[148] = t75;
  } else {
    t75 = $[148];
  }
  let t76;
  if ($[149] === Symbol.for("react.memo_cache_sentinel")) {
    t76 = <IconThird className="fillButtonIcon" />;
    $[149] = t76;
  } else {
    t76 = $[149];
  }
  let t77;
  if ($[150] !== t) {
    t77 = t("core:products.payInThree");
    $[150] = t;
    $[151] = t77;
  } else {
    t77 = $[151];
  }
  let t78;
  if ($[152] === Symbol.for("react.memo_cache_sentinel")) {
    t78 = LINKS();
    $[152] = t78;
  } else {
    t78 = $[152];
  }
  let t79;
  if ($[153] !== t77) {
    t79 = {
      analyticsParam: "rewardsPayInThree",
      icon: t76,
      testId: "rewardsPayInThree",
      title: t77,
      to: t78.payInThree
    };
    $[153] = t77;
    $[154] = t79;
  } else {
    t79 = $[154];
  }
  let t80;
  if ($[155] === Symbol.for("react.memo_cache_sentinel")) {
    t80 = <IconCard className="fillButtonIcon" />;
    $[155] = t80;
  } else {
    t80 = $[155];
  }
  let t81;
  if ($[156] !== t) {
    t81 = t("core:products.card");
    $[156] = t;
    $[157] = t81;
  } else {
    t81 = $[157];
  }
  let t82;
  if ($[158] === Symbol.for("react.memo_cache_sentinel")) {
    t82 = LINKS();
    $[158] = t82;
  } else {
    t82 = $[158];
  }
  let t83;
  if ($[159] !== t81) {
    t83 = {
      analyticsParam: "rewardsCard",
      icon: t80,
      testId: "rewardsCard",
      title: t81,
      to: t82.card
    };
    $[159] = t81;
    $[160] = t83;
  } else {
    t83 = $[160];
  }
  let t84;
  if ($[161] !== t71 || $[162] !== t75 || $[163] !== t79 || $[164] !== t83) {
    t84 = <MoreInfoSection className="relative z-10" buttons={[t71, t75, t79, t83]} />;
    $[161] = t71;
    $[162] = t75;
    $[163] = t79;
    $[164] = t83;
    $[165] = t84;
  } else {
    t84 = $[165];
  }
  let t85;
  if ($[166] === Symbol.for("react.memo_cache_sentinel")) {
    t85 = <div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" />;
    $[166] = t85;
  } else {
    t85 = $[166];
  }
  let t86;
  if ($[167] !== t67 || $[168] !== t84) {
    t86 = <div className="relative overflow-hidden bg-blue-100">{t67}{t84}{t85}</div>;
    $[167] = t67;
    $[168] = t84;
    $[169] = t86;
  } else {
    t86 = $[169];
  }
  let t87;
  if ($[170] !== menuPromoBanners || $[171] !== t12 || $[172] !== t3 || $[173] !== t50 || $[174] !== t64 || $[175] !== t86) {
    t87 = <Layout menuPromoBanners={menuPromoBanners}>{t3}{t12}{t13}{t50}{t51}{t64}{t65}{t86}</Layout>;
    $[170] = menuPromoBanners;
    $[171] = t12;
    $[172] = t3;
    $[173] = t50;
    $[174] = t64;
    $[175] = t86;
    $[176] = t87;
  } else {
    t87 = $[176];
  }
  return t87;
}
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('odmeny');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      pageBanner: await getPageBannerBySlug('rewards'),
      ...(await getIntlProps(ctx, ['rewards']))
    }
  };
}) satisfies GetStaticProps<Props>;